import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef
} from "react";
import { GOButton, GODate, GOSwitch } from "../../../components";
import * as Styled from "./styles";
import { useTranslation } from "react-i18next";
import * as StyledOfParent from "../ScheduleManageDetail/styles";
import { convertToDayWithNumber } from "../../../util/converter";
import {
  LessonProWorkTime,
  LessonProBreakTimePayload
} from "../../../Types/employeeType";
import { useDispatch } from "react-redux";
import { actionPutEmployeeLessonTime } from "../../../stores/actions";
import DatePicker from "react-datepicker";

interface boxProps {
  data?: any;
  selectedDayIndex: number;
  workTimes: [LessonProWorkTime];
  employID: number;
}

function setDateWithHourAndMin(hour: number, min: number) {
  if (hour !== null && min !== null) {
    const newDate = new Date();
    newDate.setHours(hour, min);
    return newDate;
  } else {
    return new Date();
  }
}

function resetTimeState(
  hour: null | number,
  min: null | number,
  setStateTimeValue: React.Dispatch<React.SetStateAction<Date | null>>
) {
  if (hour !== null && min != null) {
    const date = setDateWithHourAndMin(hour, min);
    setStateTimeValue(date);
  } else {
    setStateTimeValue(null);
  }
}

function makeWorkTimesPayload(
  onDutyBeginTime: Date | null,
  onDutyEndTime: Date | null,
  offDutyBeginTime: Date | null,
  offDutyEndTime: Date | null,
  selectedDayIndex: number,
  workTimes: [LessonProWorkTime]
) {
  let payload: LessonProBreakTimePayload = { workTimes: [] };
  //all items are null
  let workTimeStatus: 0 | 1 = 0;
  let breakTimeStatus: 0 | 1 = 0;

  if (
    [onDutyBeginTime, onDutyEndTime, offDutyBeginTime, offDutyEndTime].every(
      item => item === null
    )
  ) {
    workTimeStatus = 0;
    breakTimeStatus = 0;
  } else {
    if (onDutyBeginTime !== null && onDutyEndTime !== null) {
      workTimeStatus = 1;
    }
    if (offDutyBeginTime !== null && offDutyEndTime !== null) {
      breakTimeStatus = 1;
    }
  }

  payload.workTimes = workTimes.map(wt => {
    if (wt.day === selectedDayIndex) {
      return {
        ...wt,
        status: workTimeStatus,
        startHour:
          workTimeStatus === 1 ? onDutyBeginTime?.getHours() ?? null : null,
        startMinute:
          workTimeStatus === 1 ? onDutyBeginTime?.getMinutes() ?? null : null,
        endHour:
          workTimeStatus === 1 ? onDutyEndTime?.getHours() ?? null : null,
        endMinute:
          workTimeStatus === 1 ? onDutyEndTime?.getMinutes() ?? null : null,
        breakTime: {
          status: breakTimeStatus,
          startHour:
            breakTimeStatus === 1 ? offDutyBeginTime?.getHours() ?? null : null,
          startMinute:
            breakTimeStatus === 1
              ? offDutyBeginTime?.getMinutes() ?? null
              : null,
          endHour:
            breakTimeStatus === 1 ? offDutyEndTime?.getHours() ?? null : null,
          endMinute:
            breakTimeStatus === 1 ? offDutyEndTime?.getMinutes() ?? null : null
        }
      };
    } else {
      return { ...wt };
    }
  });
  return payload;
}

const ScheduleManageDetailBox = ({
  data,
  selectedDayIndex,
  workTimes,
  employID
}: boxProps) => {
  console.log("ScheduleManageDetailBox");

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const onDutyBeginTimeInputRef = useRef<DatePicker>(null);
  const onDutyEndTimeInputRef = useRef<DatePicker>(null);
  const offDutyBeginTimeInputRef = useRef<DatePicker>(null);
  const offDutyEndTimeInputRef = useRef<DatePicker>(null);

  const [onDutyBeginTime, setOnDutyBeginTime] = useState<Date | null>(null);
  const [onDutyEndTime, setOnDutyEndTime] = useState<Date | null>(null);
  const [offDutyBeginTime, setOffDutyBeginTime] = useState<Date | null>(null);
  const [offDutyEndTime, setOffDutyEndTime] = useState<Date | null>(null);

  const [isOffDuty, setIsOffDuty] = useState<boolean>(false);

  const loadStates = useCallback(
    (isIncludeSwitch: boolean) => {
      const currentWorkTime = workTimes[selectedDayIndex];
      console.log(currentWorkTime);

      if (isIncludeSwitch) {
        setIsOffDuty(currentWorkTime.status === 0);
      }
      resetTimeState(
        currentWorkTime.startHour,
        currentWorkTime.startMinute,
        setOnDutyBeginTime
      );
      resetTimeState(
        currentWorkTime.endHour,
        currentWorkTime.endMinute,
        setOnDutyEndTime
      );

      if (currentWorkTime.breakTime.status === 0) {
        resetTimeState(null, null, setOffDutyBeginTime);
        resetTimeState(null, null, setOffDutyEndTime);
      } else {
        resetTimeState(
          currentWorkTime.breakTime.startHour,
          currentWorkTime.breakTime.startMinute,
          setOffDutyBeginTime
        );
        resetTimeState(
          currentWorkTime.breakTime.endHour,
          currentWorkTime.breakTime.endMinute,
          setOffDutyEndTime
        );
      }
    },
    [workTimes, selectedDayIndex, employID]
  );

  const resetStates = useCallback(
    (isClearAll: boolean) => {
      if (isClearAll) {
        setOnDutyBeginTime(null);
        setOnDutyEndTime(null);
        setOffDutyBeginTime(null);
        setOffDutyEndTime(null);
        setIsOffDuty(true);
      } else {
        loadStates(false);
      }
    },
    [loadStates]
  );

  useEffect(() => {
    loadStates(true);
    console.log("useEffect");
  }, [selectedDayIndex, data, loadStates, employID]);

  const dayInWeek = useMemo<String>(
    () => convertToDayWithNumber(selectedDayIndex, t),
    [selectedDayIndex]
  );

  const onClickSwitch = useCallback(() => {
    console.log("onSwitch click", isOffDuty);
    const nextIsOffDuty = !isOffDuty;
    setIsOffDuty(nextIsOffDuty);
    resetStates(nextIsOffDuty);
  }, [isOffDuty, resetStates]);

  const handleCommit = useCallback(() => {
    console.log("handlecommit");
    if (isOffDuty) {
      const payload = makeWorkTimesPayload(
        null,
        null,
        null,
        null,
        selectedDayIndex,
        workTimes
      );
      dispatch(
        actionPutEmployeeLessonTime(employID, payload, () => {
          window.location.reload();
        })
      );
    } else {
      if (onDutyEndTime === null && onDutyBeginTime !== null) {
        onDutyBeginTimeInputRef.current?.setFocus();
        return;
      }
      if (onDutyEndTime !== null && onDutyBeginTime === null) {
        onDutyBeginTimeInputRef.current?.setFocus();
        return;
      }

      if (onDutyBeginTime !== null && onDutyEndTime !== null) {
        if (offDutyBeginTime !== null && offDutyEndTime === null) {
          offDutyEndTimeInputRef.current?.setFocus();
          return;
        }
        if (offDutyBeginTime === null && offDutyEndTime !== null) {
          offDutyBeginTimeInputRef.current?.setFocus();
          return;
        }
      } else if (onDutyBeginTime === null && onDutyEndTime === null) {
        if (offDutyBeginTime !== null || offDutyEndTime !== null) {
          alert(t("components.employee.enterWorkTimeOrNoBreakTime"));
          return;
        }
      }
      //check time
      if (onDutyBeginTime !== null && onDutyEndTime !== null) {
        const startTime =
          onDutyBeginTime.getHours() * 60 + onDutyBeginTime.getMinutes();
        const endTime =
          onDutyEndTime.getHours() * 60 + onDutyEndTime.getMinutes();
        if (endTime <= startTime) {
          alert(t("components.employee.checkWorkEndTime"));
          return;
        }
        if (offDutyBeginTime !== null && offDutyEndTime !== null) {
          const offStartTime =
            offDutyBeginTime.getHours() * 60 + offDutyBeginTime.getMinutes();
          const offEndTime =
            offDutyEndTime.getHours() * 60 + offDutyEndTime.getMinutes();
          if (offEndTime <= offStartTime) {
            alert(t("components.employee.checkBreakEndTime"));
            return;
          }
          if (offStartTime < startTime) {
            alert(t("components.employee.breakStartTimeBeforeWorkStartTime"));
            return;
          }
          if (offEndTime > endTime) {
            alert(t("components.employee.breakEndTimeAfterWorkEndTime"));
            return;
          }
        }
      } //endof onDutyBegintime !== null && onDutyEntime !== null
      const payload = makeWorkTimesPayload(
        onDutyBeginTime,
        onDutyEndTime,
        offDutyBeginTime,
        offDutyEndTime,
        selectedDayIndex,
        workTimes
      );
      dispatch(
        actionPutEmployeeLessonTime(employID, payload, () => {
          window.location.reload();
        })
      );
    }
  }, [
    selectedDayIndex,
    dispatch,
    employID,
    isOffDuty,
    workTimes,
    employID,
    onDutyBeginTime,
    onDutyEndTime,
    offDutyBeginTime,
    offDutyEndTime
  ]);

  return (
    <>
      <Styled.HeaderBox>
        <Styled.Day>{dayInWeek}</Styled.Day>
        <Styled.SwitchBox>
          <GOSwitch status={!isOffDuty} onClick={onClickSwitch} />
        </Styled.SwitchBox>
      </Styled.HeaderBox>
      <Styled.Title>{t("components.employee.editWorkTime")}</Styled.Title>
      <div>
        <GODate
          label={t("components.employee.startWorkTime")}
          onChange={(d: Date) => {
            console.log(d);
            setOnDutyBeginTime(d);
          }}
          time
          timeOnly
          placeholder={t("components.employee.selectWorkStartTime")}
          dataFormat="HH:mm"
          selected={onDutyBeginTime}
          disabled={isOffDuty}
          ref={onDutyBeginTimeInputRef}
        />
        <GODate
          label={t("employee.endTime")}
          onChange={(d: Date) => {
            setOnDutyEndTime(d);
          }}
          time
          timeOnly
          placeholder={t("components.employee.selectWorkEndTime")}
          dataFormat="HH:mm"
          selected={onDutyEndTime}
          disabled={isOffDuty}
          ref={onDutyEndTimeInputRef}
        />
        <hr />
        <Styled.SubTitle>
          {t("components.employee.editBreakTime")}
        </Styled.SubTitle>
        <GODate
          label={t("employee.startTime")}
          onChange={(d: Date) => {
            setOffDutyBeginTime(d);
          }}
          time
          timeOnly
          placeholder={t("components.employee.selectBreakStartTime")}
          dataFormat="HH:mm"
          selected={offDutyBeginTime}
          disabled={isOffDuty}
          ref={offDutyBeginTimeInputRef}
        />
        <GODate
          label={t("employee.endTime")}
          onChange={(d: Date) => {
            setOffDutyEndTime(d);
          }}
          time
          timeOnly
          placeholder={t("components.employee.selectBreakEndTime")}
          dataFormat="HH:mm"
          selected={offDutyEndTime}
          disabled={isOffDuty}
          ref={offDutyEndTimeInputRef}
        />
      </div>
      <StyledOfParent.ConfirmButton>
        <GOButton
          buttontype="green"
          size="medium"
          body={t("components.employee.saveWorkTime")}
          onClick={handleCommit}
          noMarginLeft
        />
      </StyledOfParent.ConfirmButton>
    </>
  );
};

export default ScheduleManageDetailBox;
